import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession, fetchUserAttributes, confirmSignIn } from 'aws-amplify/auth';
import { setCurrentTenant, resetTenantScopedCaches } from '../services/tenantScope';
import { displayName } from '../utils/displayName';
import { settingsAPI } from '../services/api';

const AuthContext = createContext(null);

// IDI-762 (D2): Manage's saved toggles, from custom:permissions (a JSON
// string). Unreadable or absent → null, which reads as every toggle on.
function parsePermissions(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

// IDI-234: the role the UI acts on. An explicit custom:role wins, then the
// first group; nothing at all is a viewer ('field_tech'), never 'admin'.
export function resolveUserRole(customRole, groups = []) {
  const explicit = typeof customRole === 'string' ? customRole.trim() : '';
  // IDI-648 (D5): super-admin comes only from a Cognito group, so a
  // custom:role naming super_admin or idi_admin counts as no role.
  const namesSuperAdmin = explicit.split(',')
    .some((r) => ['super_admin', 'idi_admin'].includes(r.trim().toLowerCase()));
  if (explicit && !namesSuperAdmin) return explicit;
  const first = (Array.isArray(groups) ? groups : []).find((g) => typeof g === 'string' && g.trim());
  return first ? first.trim() : 'field_tech';
}

// Like useAuth, but returns null outside an AuthProvider instead of throwing.
// For providers that must also work in isolated unit tests (useSettings).

// IDI-622 (D3): super-admins are never recorded (mirrors SuperAdminGuard's
// isSuperAdmin, inlined to avoid an import cycle; the server checks too).
const SUPER_ADMIN_ROLES = ['super_admin', 'idi_admin'];
function isSuperAdminUser(u) {
  return u.tenantId === 'IDI' || SUPER_ADMIN_ROLES.includes(u.role)
    || (u.groups || []).some((g) => SUPER_ADMIN_ROLES.includes(g));
}

// IDI-622 (D3, F2): BIDIT's own Last Login record on every sign-in or app
// open. Fire-and-forget: never awaited, every failure swallowed, so a failed
// record can never fail or slow a sign-in.
function recordLoginQuietly(u) {
  try {
    if (!u || isSuperAdminUser(u) || typeof settingsAPI?.recordLogin !== 'function') return;
    Promise.resolve(settingsAPI.recordLogin()).catch(() => {});
  } catch (_) {
    // swallowed on purpose (F2)
  }
}

export const useOptionalAuth = () => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // IDI-815 (D4): starts disarmed. Only a signed-in session can expire, so a
  // 401 before any sign-in stores nothing; checkAuth's success arms it.
  const sessionExpiredHandledRef = useRef(true);
  const [loading, setLoading] = useState(true);

  // Check auth state on mount
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkAuth is stable via useCallback below; declared after this effect for readability
  }, []);

  // IDI-121 half A (2026-09-03): listen for the api.js session-expired event.
  //
  // When any /v1/* endpoint returns 401 (except the /auth/login family),
  // api.js dispatches `auth:session-expired`. We respond by calling logout(),
  // which clears state and sets isAuthenticated=false; the route guard at
  // App.js:~100 then redirects to /login.
  //
  // Idempotency: multiple 401s in rapid succession (e.g. a screen firing
  // 5 concurrent /v1 requests as its ID token expires) can dispatch the
  // event multiple times. logout() is idempotent — setting isAuthenticated
  // to false a second time is a no-op — but Amplify's signOut may throw
  // on a second call; the logout function already swallows that in its
  // finally block.
  //
  // Test env note: window.CustomEvent exists in jsdom, so listeners fire
  // in unit tests. See AuthContext.session-expired.test.js.
  useEffect(() => {
    const handleSessionExpired = () => {
      // Coalesce: a page fires many requests at once, so an expired session
      // produces a burst of 401s. Only the first one signs out; the rest are
      // no-ops until a new login re-arms it (checkAuth success). Without this,
      // every 401 re-ran logout() and its cache reset.
      if (sessionExpiredHandledRef.current) return;
      sessionExpiredHandledRef.current = true;
      // eslint-disable-next-line no-console -- one-line breadcrumb for
      // devtools sessions where the redirect is otherwise mysterious.
      console.warn('[auth] session expired; signing out and redirecting to /login');
      // IDI-121 req. 2: say so ONCE, clearly. Survives the redirect to /login
      // (sessionStorage) and is consumed there, so it shows exactly once. Only
      // reached after api.js's refresh-and-retry has already failed.
      try { sessionStorage.setItem('bidit:signedOutReason', 'expired'); } catch { /* ignore */ }
      logout();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('auth:session-expired', handleSessionExpired);
      return () => {
        window.removeEventListener('auth:session-expired', handleSessionExpired);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- logout is stable via useCallback below
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();

      // Cognito Groups are delivered in the token payload (cognito:groups), not
      // via fetchUserAttributes(). They are the primary role mechanism (the
      // backend reads cognito:groups first), so surface them here.
      const idClaims = session.tokens?.idToken?.payload || {};
      const groups = idClaims['cognito:groups'] || [];
      const groupList = Array.isArray(groups)
        ? groups
        : (typeof groups === 'string' ? groups.split(',').map((g) => g.trim()).filter(Boolean) : []);
      // Prefer an explicit custom:role, otherwise fall back to the first group.
      // IDI-234 (RULED, Adrian 2026-10-16): least privilege. A user with NO
      // role gets the field-tech (viewer) rules until an admin sets one; the
      // jobs/invoices API enforces the same. (It used to default to 'admin',
      // so the UI offered controls the server refuses.)
      const role = resolveUserRole(attributes['custom:role'], groupList);

      const tenantId = attributes['custom:tenant_id'] || idClaims['custom:tenant_id'] || 'default';
      // IDI-304: scope every module-level tenant cache to THIS signed-in
      // identity. Keyed on tenant + Cognito user id, not tenant alone: the
      // tenant can fall back to 'default' for users without the claim, and
      // two dealers colliding on 'default' must still not share a cache.
      setCurrentTenant(`${tenantId}::${currentUser.userId}`);
      sessionExpiredHandledRef.current = false; // re-arm for this session

      const nextUser = {
        id: currentUser.userId,
        username: currentUser.username,
        email: attributes.email,
        firstName: attributes.given_name || '',
        lastName: attributes.family_name || '',
        // IDI-550: the shown name by the one rule (profile given + family →
        // invite name → email). Note writers, the time log and Record
        // Decision read this; never the email's local part, never 'You'.
        name: displayName({
          firstName: attributes.given_name,
          lastName: attributes.family_name,
          name: attributes.name,
          email: attributes.email,
        }),
        tenantId,
        role,
        groups: groupList,
        permissions: parsePermissions(attributes['custom:permissions'] ?? idClaims['custom:permissions']),
        accessToken: session.tokens?.accessToken?.toString(),
        idToken: session.tokens?.idToken?.toString(),
      };
      setUser(nextUser);
      setIsAuthenticated(true);
      recordLoginQuietly(nextUser);
      // IDI-582: returned so callers (Login) can route on the fresh user
      // without waiting for the state update to re-render them.
      return nextUser;
    } catch (err) {
      // Not authenticated
      setCurrentTenant(null);
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      // Sign out any existing session first to prevent UserAlreadyAuthenticatedException
      try { await signOut(); } catch { /* ignore */ }
      // IDI-44: drop any tenant cached from a prior session so the new user's
      // JWT tenant claim is the only source of truth for X-Tenant-Id.
      try { localStorage.removeItem('tenantId'); } catch { /* ignore */ }

      const result = await signIn({ username: email, password });
      
      if (result.isSignedIn) {
        const signedIn = await checkAuth();
        return { success: true, user: signedIn };
      }
      
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return { success: false, challenge: 'CONFIRM_SIGN_UP', message: 'Please verify your email first' };
      }
      
      // IDI-815 (D1): Amplify 6 names the temporary-password step
      // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED; Login.js keeps its own name.
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { success: false, challenge: 'NEW_PASSWORD_REQUIRED', message: 'Password change required' };
      }

      return { success: false, message: 'Login failed' };
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'NotAuthorizedException') {
        return { success: false, message: 'Invalid email or password' };
      }
      if (err.name === 'UserNotFoundException') {
        return { success: false, message: 'No account found with that email' };
      }
      if (err.name === 'UserNotConfirmedException') {
        return { success: false, message: 'Please verify your email first', challenge: 'CONFIRM_SIGN_UP' };
      }
      return { success: false, message: err.message || 'Login failed' };
    }
  }, [checkAuth]);

  const logout = useCallback(async () => {
    // IDI-815 (D4): a chosen Sign out is not an expiry. Disarm so a 401 after
    // it stores nothing until the next sign-in re-arms (checkAuth). A real
    // expiry has already stored its notice before calling here; keep it.
    sessionExpiredHandledRef.current = true;
    try {
      await signOut();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // IDI-44: clear the cached tenant so the next login can't inherit it.
      try { localStorage.removeItem('tenantId'); } catch { /* ignore */ }
      // IDI-304: ...and every in-memory tenant cache (company profile,
      // branding, settings lists). setCurrentTenant(null) resets them on a
      // change; the explicit reset also covers a re-login as the SAME tenant
      // after an in-place data change.
      setCurrentTenant(null);
      resetTenantScopedCaches();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.accessToken?.toString();
    } catch {
      return null;
    }
  }, []);

  const getIdToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString();
    } catch {
      return null;
    }
  }, []);

  // PT-48: Complete NEW_PASSWORD_REQUIRED challenge (first login after invite)
  const completeNewPassword = useCallback(async (newPassword) => {
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        const signedIn = await checkAuth();
        return { success: true, user: signedIn };
      }
      return { success: false, message: 'Password change failed' };
    } catch (err) {
      console.error('Complete new password error:', err);
      return { success: false, message: err.message || 'Failed to set new password' };
    }
  }, [checkAuth]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    completeNewPassword,
    getAccessToken,
    getIdToken,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
